import {trackEvent} from '@hconnect/common/logging/Analytics'
import type {DateTimeForm} from '@hconnect/tuc-processes/fields/dateTime/types'
import type {
  SpacingOrFlowRate,
  TimeOnJobSite
} from '@hconnect/tuc-processes/fields/loadSpacing/types'
import type {MixForm} from '@hconnect/tuc-processes/fields/mix/types'
import type {MixCharacteristicsForm} from '@hconnect/tuc-processes/fields/mixCharacteristics/types'
import type {PlusLoadForm} from '@hconnect/tuc-processes/fields/plusLoad/types'
import type {QuantitySelection} from '@hconnect/tuc-processes/fields/quantity/types'
import type {RetarderForm} from '@hconnect/tuc-processes/fields/retarder/types'
import type {
  AdditionalNotesForm,
  DriverInstructionsForm,
  PoNumberForm
} from '@hconnect/tuc-processes/fields/textField/types'
import type {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import type {ConcreteUsageForm} from '@hconnect/tuc-processes/fields/usage/types'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import moment from 'moment'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {formattedDate} from '../../../../../Pages/Order/OrderDetails/components/utils'
import {getTranslatedErrorMessage} from '../../../orderIntake/utils/errorTranslation'
import type {RmcOrderUpdateOptions} from '../../../orderIntake/types/order'
import {formatQuantityDisplay} from '../utils/quantity.utils'
import {getUnloadingMethodText} from '../components/fields/UnloadingMethod/unloadingMethodUtils'
import {EditableFieldType} from '../RmcOrderEditStepView'

type QuantityValue = QuantitySelection
type PlusLoadValue = PlusLoadForm['plusLoadExpected']
type UnloadingMethodValue = string | undefined
type TimeOnJobSiteValue = TimeOnJobSite | null | undefined
type SpacingOrFlowRateValue = SpacingOrFlowRate | null | undefined
type DateTimeValue = DateTimeForm['dateTime'] | null | undefined
type PoNumberValue = PoNumberForm['poNumber']
type DriverInstructionsValue = DriverInstructionsForm['driverInstructions']
type AdditionalNotesValue = AdditionalNotesForm['additionalNotes']
type UsageValue = ConcreteUsageForm['concreteUsage']
type MaterialValue = MixForm['material'] | null | undefined
type MaterialVariationsValue = MixCharacteristicsForm['materialVariations'] | null | undefined
type RetarderValue = RetarderForm['retarder'] | null | undefined

type OrderEditFieldValues = {
  quote?: string | null
  quantitySelection?: QuantityValue | null
  plusLoad?: PlusLoadValue | null
  unloadingMethod?: UnloadingMethodValue
  timeOnJobSite?: TimeOnJobSiteValue
  spacingOrFlowRate?: SpacingOrFlowRateValue
  dateTime?: DateTimeValue
  poNumber?: PoNumberValue
  driverInstructions?: DriverInstructionsValue
  additionalNotes?: AdditionalNotesValue
  usage?: UsageValue
  material?: MaterialValue
  materialVariations?: MaterialVariationsValue
  retarder?: RetarderValue
}

interface UseRmcOrderEditLogicProps {
  orderId: string // for analytics only?
  onOrderUpdate?: (formData: FieldsForm, options?: RmcOrderUpdateOptions) => void
  onClose: () => void
  fieldValues: OrderEditFieldValues
  requiresDispatchApproval?: boolean
}

export const useRmcOrderEditLogic = ({
  orderId,
  onOrderUpdate,
  onClose,
  fieldValues,
  requiresDispatchApproval
}: UseRmcOrderEditLogicProps) => {
  const {t, i18n} = useTranslation()

  // Constants
  const NOT_SPECIFIED = t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')

  // direct actions on a form, maybe we can move that to the package?
  const {reset, trigger, formState, getValues} = useOrderProcessFormContext()

  const {
    quote,
    quantitySelection,
    plusLoad,
    unloadingMethod,
    timeOnJobSite,
    spacingOrFlowRate,
    dateTime,
    poNumber,
    driverInstructions,
    additionalNotes,
    usage,
    material,
    materialVariations,
    retarder
  } = fieldValues

  // Get validation errors with elegant translation
  const retarderError = getTranslatedErrorMessage(formState.errors?.retarder, t)
  const driverInstructionsError = getTranslatedErrorMessage(formState.errors?.driverInstructions, t)
  const additionalNotesError = getTranslatedErrorMessage(formState.errors?.additionalNotes, t)

  const displayValues = React.useMemo(() => {
    const withFallback = (value?: string | null) => {
      const trimmed = value?.toString().trim()
      return trimmed && trimmed.length > 0 ? trimmed : NOT_SPECIFIED
    }

    const spacingInfo = (() => {
      if (!spacingOrFlowRate) return null

      const isLoadSpace = spacingOrFlowRate.type === 'loadSpace'
      const typeLabel = isLoadSpace
        ? t('orderIntake.rmc.orderManagement.spacing', 'loadSpace')
        : t('orderIntake.rmc.orderManagement.flowRate', 'Flow rate')
      const value = isLoadSpace ? spacingOrFlowRate.loadSpace : spacingOrFlowRate.flowRate
      const unit = isLoadSpace ? spacingOrFlowRate.loadSpaceUom : spacingOrFlowRate.flowRateUom

      if (value === null || value === undefined || !unit) {
        return null
      }

      return `${typeLabel} - ${value} ${unit}`
    })()

    const timeOnSiteInfo = timeOnJobSite
      ? `${t('orderIntake.rmc.orderManagement.timeOnSite', 'Time on site')} - ${
          timeOnJobSite.time
        } ${timeOnJobSite.timeUom}`
      : null

    const intervalsValue = (() => {
      const parts = [spacingInfo, timeOnSiteInfo].filter(Boolean) as string[]
      return parts.length > 0 ? parts.join(', ') : NOT_SPECIFIED
    })()

    const materialVariationsDisplay = (() => {
      if (!materialVariations) return NOT_SPECIFIED

      const hasSlump =
        materialVariations.slump?.value !== undefined && materialVariations.slump?.value !== null
      const hasSetControl =
        materialVariations.setControl?.value !== undefined &&
        materialVariations.setControl?.value !== null

      if (!hasSlump && !hasSetControl) return NOT_SPECIFIED

      const parts: string[] = []
      if (hasSlump) {
        const slumpValue = materialVariations.slump?.description || materialVariations.slump?.value
        if (slumpValue) parts.push(slumpValue)
      }
      if (hasSetControl) {
        const setControlValue =
          materialVariations.setControl?.description || materialVariations.setControl?.value
        if (setControlValue) parts.push(setControlValue)
      }

      return parts.length > 0 ? parts.join(', ') : NOT_SPECIFIED
    })()

    const quantityDisplay = quantitySelection
      ? (() => {
          let value = formatQuantityDisplay(quantitySelection, t)
          if (plusLoad) {
            value += ` + ${t('orderIntake.rmc.orderManagement.plusLoad', 'plus load')}`
          }
          return value
        })()
      : NOT_SPECIFIED

    const unloadingDisplay =
      getUnloadingMethodText(t, unloadingMethod ?? undefined) || NOT_SPECIFIED

    const dateDisplay =
      dateTime && dateTime[0] ? moment(dateTime[0]).format('DD.MM.YYYY') : NOT_SPECIFIED

    const timeDisplay =
      dateTime && dateTime[0] ? formattedDate(dateTime[0], i18n.language)[1] : NOT_SPECIFIED

    const retarderValue =
      retarder?.retarder !== undefined && retarder?.retarder !== null
        ? `${retarder.retarder}${retarder.retarderUom ? ` ${retarder.retarderUom}` : ''}`
        : NOT_SPECIFIED

    return {
      [EditableFieldType.QUOTE]: withFallback(quote),
      [EditableFieldType.MATERIAL]: material?.materialDescription ?? '',
      [EditableFieldType.MIX_CHARACTERISTICS]: materialVariationsDisplay,
      [EditableFieldType.QUANTITY]: quantityDisplay,
      [EditableFieldType.USAGE]: withFallback(usage),
      [EditableFieldType.UNLOADING_METHOD]: unloadingDisplay,
      [EditableFieldType.INTERVALS]: intervalsValue,
      [EditableFieldType.DATE]: dateDisplay,
      [EditableFieldType.TIME]: timeDisplay,
      [EditableFieldType.PO_NUMBER]: withFallback(poNumber),
      [EditableFieldType.DRIVER_INSTRUCTIONS]: withFallback(driverInstructions),
      [EditableFieldType.ADDITIONAL_NOTES]: withFallback(additionalNotes),
      [EditableFieldType.RETARDER]: retarderValue
    }
  }, [
    additionalNotes,
    dateTime,
    driverInstructions,
    i18n.language,
    material?.materialDescription,
    materialVariations,
    NOT_SPECIFIED,
    plusLoad,
    poNumber,
    quantitySelection,
    quote,
    retarder?.retarder,
    retarder?.retarderUom,
    spacingOrFlowRate,
    t,
    timeOnJobSite,
    unloadingMethod,
    usage
  ])

  const getStepDisplayValue = React.useCallback(
    (editStepType: EditableFieldType): string => {
      return displayValues[editStepType] ?? NOT_SPECIFIED
    },
    [displayValues, NOT_SPECIFIED]
  )

  const getIntervalsDisplayValue = React.useCallback(() => {
    return displayValues[EditableFieldType.INTERVALS]
  }, [displayValues])

  // Check if there are any unsaved changes
  const hasUnsavedChanges = React.useMemo(() => {
    return formState.isDirty
  }, [formState.isDirty])

  // Submit handler
  const handleSubmitAndSave = async () => {
    const isValid = await trigger()

    if (onOrderUpdate) {
      onOrderUpdate(getValues(), {requiresDispatchApproval, isValid})
    }

    if (!isValid) {
      return
    }

    // Track order save event
    trackEvent('rmcOrderSaved', {
      orderId: orderId
    })

    onClose()
  }

  // Discard changes handler
  const handleDiscardChanges = () => {
    if (onOrderUpdate) {
      onOrderUpdate(formState.defaultValues as FieldsForm)
    }
    reset()
    onClose()
  }

  return {
    // State
    hasUnsavedChanges,

    // Display functions
    getStepDisplayValue,
    getIntervalsDisplayValue,

    // Event handlers
    handleSubmitAndSave,
    handleDiscardChanges,

    // Validation errors
    retarderError,
    driverInstructionsError,
    additionalNotesError,

    formState,
    reset
  }
}
